// useEscapeStack.ts — universal ONE-LEVEL Esc dismissal.
// CONTRACT: docs/ux/SHELL-COMPOSITION.md §4(4)
//
// THE LAW: "Esc = universal one-level dismiss everywhere: result list → panel →
// filter → deselect." A single Esc press pops EXACTLY ONE level — never two,
// never a cascade. The cmdk base already did `Escape → setOpen(false)`; this
// hook generalises it so every dismissible level (Throughline dropdown, Ledger,
// Catalog flip, an active FILTER, a selection) registers itself and the levels
// pop in strict LIFO order.
//
// MECHANISM: one shared LIFO stack + ONE global `keydown` listener for the whole
// shell. A component calls `useEscapeStack(onEscape, active)`; while `active` is
// true its handler sits on top of the stack. The single Esc keypress invokes ONLY
// the top-most handler and stops there (the event is consumed). Whichever handler
// most-recently became active wins — matching the visual "innermost surface
// dismisses first" expectation.
//
// No host, no MapLibre, no design tokens — pure React + DOM keyboard wiring.

import { useEffect, useRef } from "react";

/** A registered Esc handler. The component owns `onEscape`; the ref identity is
 *  what we push/pop so a re-render with a new closure doesn't reorder the stack. */
interface EscEntry {
  handler: () => void;
}

// The shared LIFO stack. The LAST entry is the top-most (innermost) level and is
// the only one a single Esc press fires.
const stack: EscEntry[] = [];

let globalListenerAttached = false;

function onGlobalKeydown(e: KeyboardEvent): void {
  if (e.key !== "Escape") return;
  const top = stack[stack.length - 1];
  if (!top) return;
  // Consume the event so exactly ONE level pops — no other Esc listener (or a
  // parent level lower in the stack) also fires for this single press.
  e.preventDefault();
  e.stopPropagation();
  top.handler();
}

function ensureGlobalListener(): void {
  if (globalListenerAttached) return;
  // Capture phase so the shell's one-level rule wins over component-local bubble
  // listeners that might otherwise dismiss a second level on the same press.
  window.addEventListener("keydown", onGlobalKeydown, true);
  globalListenerAttached = true;
}

function teardownGlobalListenerIfIdle(): void {
  if (!globalListenerAttached || stack.length > 0) return;
  window.removeEventListener("keydown", onGlobalKeydown, true);
  globalListenerAttached = false;
}

/**
 * Register a one-level Esc dismiss handler.
 *
 * @param onEscape  called when Esc is pressed AND this entry is the top of the
 *                  stack. Should pop exactly THIS level (e.g. close the dropdown,
 *                  clear the filter, or deselect) — never cascade.
 * @param active    whether this level is currently dismissible. When false the
 *                  entry is OFF the stack (so Esc reaches the next level down).
 *                  Defaults to true.
 *
 * Ordering is strict LIFO by activation: the surface that most-recently became
 * active is dismissed first. Always pair `active` with the surface's open state
 * so closing a level hands Esc to its parent automatically.
 */
export function useEscapeStack(onEscape: () => void, active: boolean = true): void {
  // Keep the latest closure in a stable ref so the stack entry identity never
  // changes across re-renders — only `active` pushes/pops it.
  const handlerRef = useRef(onEscape);
  handlerRef.current = onEscape;

  // One stable entry per hook instance for its whole lifetime.
  const entryRef = useRef<EscEntry | null>(null);
  if (entryRef.current === null) {
    entryRef.current = { handler: () => handlerRef.current() };
  }

  useEffect(() => {
    if (!active) return;
    const entry = entryRef.current!;
    stack.push(entry);
    ensureGlobalListener();
    return () => {
      const i = stack.lastIndexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      teardownGlobalListenerIfIdle();
    };
  }, [active]);
}
